import React from 'react';
import type { ReactNode } from 'react';

import { LinkButton } from '@atlaskit/button/new';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import type { NewCoreIconProps } from '@atlaskit/icon';

import { ConfluencePageSubType } from '@confluence/create-blank-fabric-page/entry-points/CreateBlankPageMutation.ts';
import { buildCreationLink } from '@confluence/create-blank-fabric-page/entry-points/createContentRouteUtils.ts';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled.ts';

import { useHTV2State } from '../../useHTV2Store.ts';

type HTV2CreateContentCTAProps = {
	appearance: 'primary' | 'default';
	children: ReactNode;
	contentType: 'page' | 'whiteboard';
	onClick: () => void;
	source: string;
	icon?: React.ComponentType<NewCoreIconProps> | undefined;
	openTemplatesGallery?: boolean;
};

type CtaComponentProps = { children: ReactNode; onClick: () => void };

const HTV2CreateContentCTA = ({
	appearance,
	children,
	contentType,
	onClick,
	source,
	icon,
	openTemplatesGallery,
}: HTV2CreateContentCTAProps) => {
	const { personalSpaceKey } = useHTV2State();

	const { isLivePagesFeatureEnabled } = useIsLivePagesFeatureEnabled();

	const getButtonHref = (): string => {
		return buildCreationLink({
			contentType,
			source,
			spaceKey: personalSpaceKey,
			subType: isLivePagesFeatureEnabled ? ConfluencePageSubType.LIVE : undefined,
			openTemplatesGallery: openTemplatesGallery || undefined,
		});
	};

	return (
		<LinkButton
			onClick={onClick}
			isDisabled={!Boolean(personalSpaceKey)}
			href={getButtonHref()}
			appearance={appearance}
			iconBefore={icon}
		>
			{children}
		</LinkButton>
	);
};

export const HTV2CreateWhiteboardCTA = ({ children, onClick }: CtaComponentProps) => {
	return (
		<HTV2CreateContentCTA
			source="homeOnboardingTakeoverV2CreateWhiteboard"
			contentType="whiteboard"
			onClick={onClick}
			appearance="default"
			icon={WhiteboardIcon}
		>
			{children}
		</HTV2CreateContentCTA>
	);
};

export const HTV2CreateIntroPageCTA = ({ children, onClick }: CtaComponentProps) => {
	return (
		<HTV2CreateContentCTA
			source="homeOnboardingTakeoverV2CreateIntro"
			contentType="page"
			onClick={onClick}
			appearance="default"
			openTemplatesGallery
		>
			{children}
		</HTV2CreateContentCTA>
	);
};
